import React, { useState } from "react";
import { trips, species, photos, IMG } from "../data/mock";
import { Eyebrow } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { useDeposit } from "../context/DepositContext";
import { Check, ArrowRight, ArrowLeft, Anchor, Clock, Users, Fish, Waves, Compass, MessageCircle } from "lucide-react";

const STEPS = ["Your Details", "Experience", "Trip", "Target Fish", "Review", "Book"];

const experiences = [
  { id: "fishing", icon: Fish, title: "Serious Offshore", desc: "Chase billfish, tuna & dorado in the blue water.", suggest: "full-day-offshore", img: photos.marlin },
  { id: "inshore", icon: Anchor, title: "Inshore Adventure", desc: "Roosterfish, snapper & jacks along the coast all day.", suggest: "full-day-inshore", img: photos.roosterBeard },
  { id: "taste", icon: Compass, title: "A Taste of It All", desc: "Fishing + eco tour in a fast half day.", suggest: "half-day-tour", img: photos.jackShirtless },
  { id: "surf", icon: Waves, title: "Surf & Explore", desc: "Boat access to uncrowded surf breaks.", suggest: "surf-drops", img: photos.beachTona },
  { id: "chill", icon: Fish, title: "Relax & Celebrate", desc: "Sunset cruise with drinks & good vibes.", suggest: "sunset-cruise", img: IMG.sunset },
];

const PlanYourTrip = () => {
  const { openDeposit } = useDeposit();
  const [step, setStep] = useState(0);
  const [exp, setExp] = useState(null);
  const [tripSlug, setTripSlug] = useState(null);
  const [zone, setZone] = useState("offshore");
  const [picks, setPicks] = useState([]);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadSaved, setLeadSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leadError, setLeadError] = useState(null);
  const setL = (k, v) => setLead((p) => ({ ...p, [k]: v }));

  const submitLead = async () => {
    setLeadError(null);
    if (!lead.name.trim() || !lead.phone.trim() || !lead.email.trim()) {
      setLeadError("Please add your name, phone and email so we can reach you.");
      return;
    }
    if (!leadSaved) {
      setSaving(true);
      try {
        await api.createLead({ name: lead.name.trim(), email: lead.email.trim(), phone: lead.phone.trim(), trip: "Plan Your Trip", message: "Started the Plan Your Trip flow.", source: "plan" });
        setLeadSaved(true);
      } catch (e) { /* don't block planning if the save fails */ }
      finally { setSaving(false); }
    }
    next();
  };

  const trip = trips.find((t) => t.slug === tripSlug);
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const chooseExp = (e) => { setExp(e.id); setTripSlug(e.suggest); setTimeout(next, 150); };
  const chooseTrip = (slug) => { setTripSlug(slug); setTimeout(next, 150); };
  const togglePick = (slug) => setPicks((p) => p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]);

  const bookSummary = () => {
    if (!trip) return "";
    const expLabel = experiences.find((e) => e.id === exp)?.title;
    const speciesNames = picks.map((p) => species.find((x) => x.slug === p)?.name).filter(Boolean);
    return [
      lead.name ? `• Name: ${lead.name}` : null,
      lead.phone ? `• Phone: ${lead.phone}` : null,
      `• Trip: ${trip.name} ($${trip.price}, ${trip.duration})`,
      expLabel ? `• Vibe: ${expLabel}` : null,
      speciesNames.length ? `• Target fish: ${speciesNames.join(", ")}` : null,
      `Please help me pick an available date. Thank you!`,
    ].filter(Boolean).join("\n");
  };

  return (
    <section className="py-16 min-h-[80vh]">
      <div className="container-x max-w-4xl">
        <div className="text-center">
          <Eyebrow>Guided booking</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-white">Plan Your Trip</h1>
          <p className="text-slate-300 mt-2">A few quick steps and you'll be ready to reserve your Pacific adventure.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 my-9 flex-wrap">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-heading transition ${i===step?"bg-[#f5a524] text-[#0a1f33]":i<step?"bg-[#0e9488] text-white":"bg-[#0e2740] text-slate-400 border border-white/10"}`}>
                <span className="h-5 w-5 rounded-full bg-black/15 flex items-center justify-center text-[11px]">{i<step?<Check className="h-3 w-3" />:i+1}</span>
                {label}
              </div>
              {i<STEPS.length-1 && <div className={`h-px w-4 ${i<step?"bg-[#0e9488]":"bg-white/15"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0e2740] p-6 md:p-9">
          {/* Step 0: Your Details (lead capture) */}
          {step === 0 && (
            <div>
              <h2 className="font-heading text-2xl text-white mb-1">First, who are we planning for?</h2>
              <p className="text-slate-400 text-sm mb-6">Share your details so our captain can hold your spot and reply fast — no spam, ever.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-300">Full name</Label><Input value={lead.name} onChange={(e)=>setL("name",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder="Your name" data-testid="plan-lead-name" /></div>
                <div><Label className="text-slate-300">Phone / WhatsApp</Label><Input value={lead.phone} onChange={(e)=>setL("phone",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder="+1 555 000 1234" data-testid="plan-lead-phone" /></div>
              </div>
              <div className="mt-4"><Label className="text-slate-300">Email</Label><Input type="email" value={lead.email} onChange={(e)=>setL("email",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder="you@email.com" data-testid="plan-lead-email" /></div>
              {leadError && <p className="text-red-300 text-sm mt-3" data-testid="plan-lead-error">{leadError}</p>}
              <div className="flex justify-end mt-7"><Button className="btn-gold gap-1" disabled={saving} onClick={submitLead} data-testid="plan-lead-continue">{saving ? "Saving..." : "Start planning"} <ArrowRight className="h-4 w-4" /></Button></div>
            </div>
          )}

          {/* Step 1: Experience */}
          {step === 1 && (
            <div>
              <h2 className="font-heading text-2xl text-white mb-1">What kind of day are you after?</h2>
              <p className="text-slate-400 text-sm mb-6">Pick the vibe and we'll suggest the perfect trip.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {experiences.map((e) => (
                  <button key={e.id} onClick={() => chooseExp(e)} className={`group relative text-left rounded-2xl border overflow-hidden transition card-hover ${exp===e.id?"border-[#f5a524] ring-2 ring-[#f5a524]/40":"border-white/10 hover:border-white/25"}`}>
                    <div className="relative h-40">
                      <img src={e.img} alt={e.title} className="absolute inset-0 h-full w-full object-cover object-center transition duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/50 to-transparent" />
                      <e.icon className="absolute top-3 left-3 h-7 w-7 text-[#f5a524]" />
                      {exp===e.id && <span className="absolute top-3 right-3 rounded-full bg-[#f5a524] p-1"><Check className="h-3.5 w-3.5 text-[#0a1f33]" /></span>}
                      <div className="absolute bottom-0 p-4">
                        <h3 className="font-heading text-xl text-white">{e.title}</h3>
                        <p className="text-sm text-slate-300 mt-0.5">{e.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-start mt-7"><Button variant="ghost" onClick={back} className="text-slate-300 hover:text-white gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button></div>
            </div>
          )}

          {/* Step 2: Trip */}
          {step === 2 && (
            <div>
              <h2 className="font-heading text-2xl text-white mb-1">Choose your trip</h2>
              <p className="text-slate-400 text-sm mb-6">We recommend one based on your choice — change it any time.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {trips.map((t) => (
                  <button key={t.slug} onClick={() => chooseTrip(t.slug)} className={`text-left rounded-2xl border overflow-hidden transition card-hover ${tripSlug===t.slug?"border-[#f5a524]":"border-white/10 hover:border-white/25"}`}>
                    <div className="relative h-32"><img src={t.image} alt={t.name} className="h-full w-full object-cover object-center" /><span className="absolute top-2 right-2 rounded-full bg-[#f5a524] px-2 py-0.5 text-xs font-bold text-[#0a1f33]">${t.price}</span></div>
                    <div className="p-4"><h3 className="font-heading text-lg text-white">{t.name}</h3><p className="text-xs text-slate-400 flex gap-3 mt-1"><span className="flex items-center gap-1"><Clock className="h-3 w-3" />{t.duration}</span><span className="flex items-center gap-1"><Users className="h-3 w-3" />12</span></p></div>
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-7"><Button variant="ghost" onClick={back} className="text-slate-300 hover:text-white gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button><Button className="btn-gold gap-1" disabled={!tripSlug} onClick={next}>Continue <ArrowRight className="h-4 w-4" /></Button></div>
            </div>
          )}

          {/* Step 3: Target Fish */}
          {step === 3 && (
            <div>
              <h2 className="font-heading text-2xl text-white mb-1">Which fish are you dreaming about?</h2>
              <p className="text-slate-400 text-sm mb-4">Optional — helps our captain plan your day. Pick as many as you like.</p>
              <div className="flex gap-2 mb-5">
                {[["offshore","Offshore"],["inshore","Inshore"]].map(([k,l])=>(<button key={k} onClick={()=>setZone(k)} className={`rounded-full px-4 py-1.5 text-sm font-heading ${zone===k?"bg-[#f5a524] text-[#0a1f33]":"bg-white/5 text-slate-200 border border-white/10"}`}>{l}</button>))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {species.filter((s)=>s.zone===zone).map((s)=>(
                  <button key={s.slug} onClick={()=>togglePick(s.slug)} className={`relative rounded-xl overflow-hidden h-28 bg-[#0a1f33] border transition ${picks.includes(s.slug)?"border-[#f5a524] ring-2 ring-[#f5a524]/40":"border-white/10"}`}>
                    <img src={s.image} alt={s.name} className="absolute inset-0 h-full w-full object-contain p-1.5" />
                    <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/70 to-transparent" />
                    {picks.includes(s.slug) && <span className="absolute top-2 right-2 rounded-full bg-[#f5a524] p-1"><Check className="h-3 w-3 text-[#0a1f33]" /></span>}
                    <span className="absolute bottom-2 left-2 font-heading text-white text-sm">{s.name}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-7"><Button variant="ghost" onClick={back} className="text-slate-300 hover:text-white gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button><Button className="btn-gold gap-1" onClick={next}>Continue <ArrowRight className="h-4 w-4" /></Button></div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && trip && (
            <div>
              <h2 className="font-heading text-2xl text-white mb-6">Review your adventure</h2>
              <div className="flex flex-col sm:flex-row gap-5">
                <img src={trip.image} alt={trip.name} className="h-40 sm:w-56 w-full rounded-2xl object-cover object-center" />
                <div className="flex-1">
                  <p className="text-[#f5a524] text-xs font-heading tracking-widest uppercase">{trip.hook}</p>
                  <h3 className="font-heading text-2xl text-white">{trip.name}</h3>
                  <p className="text-sm text-slate-300 mt-1">{trip.duration} · {trip.capacity}</p>
                  <p className="text-sm text-slate-300 mt-3">{trip.short}</p>
                  {picks.length>0 && (
                    <div className="mt-3"><p className="text-xs text-slate-400 mb-1">Target species:</p><div className="flex flex-wrap gap-1.5">{picks.map((p)=>{const s=species.find(x=>x.slug===p);return <span key={p} className="rounded-full bg-white/5 border border-white/10 px-2.5 py-0.5 text-xs text-slate-200">{s?.name}</span>;})}</div></div>
                  )}
                </div>
                <div className="text-right"><p className="text-xs text-slate-400">From</p><p className="font-display text-4xl text-[#f5a524]">${trip.price}</p><p className="text-xs text-slate-400">private charter</p></div>
              </div>
              <div className="flex justify-between mt-7"><Button variant="ghost" onClick={back} className="text-slate-300 hover:text-white gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button><Button className="btn-gold gap-1" onClick={next}>Looks good <ArrowRight className="h-4 w-4" /></Button></div>
            </div>
          )}

          {/* Step 5: Book */}
          {step === 5 && trip && (
            <div className="text-center py-4">
              <div className="inline-flex rounded-2xl bg-[#25D366]/15 p-4 text-[#25D366] mb-4"><MessageCircle className="h-10 w-10" /></div>
              <h2 className="font-heading text-3xl text-white">Ready to book!</h2>
              <p className="text-slate-300 mt-2 max-w-md mx-auto">Send us a quick WhatsApp to lock in your <b className="text-white">{trip.name}</b>. We'll confirm your date and answer any questions — usually within minutes.</p>
              <Button className="btn-gold h-14 px-10 text-base mt-6 gap-2" onClick={() => openDeposit(trip, bookSummary())}><MessageCircle className="h-5 w-5" /> Book on WhatsApp</Button>
              <div><Button variant="ghost" onClick={back} className="text-slate-400 hover:text-white mt-4 gap-1"><ArrowLeft className="h-4 w-4" /> Back to review</Button></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PlanYourTrip;
